import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import { BodyCopy } from '@xingternal/typography';

import { MessageContainer } from '../Components';

import GET_VIDEO from '../Video.graphql';
import CREATE_VIDEO_TRACKING_EVENT from '../VideoTrackingEvent.graphql';

import Player from './Player';

import PreviewMode from './PreviewMode';
import Encoding from './Encoding';

const getSources = (sources) => {
  let sourceList = [];

  if (sources) {
    const hls = sources.find(
      (item) => item.format === 'application/x-mpegURL'
    )?.source;
    const dash = sources.find(
      (item) => item.format === 'application/dash+xml'
    )?.source;

    hls && sourceList.push(hls);
    dash && sourceList.push(dash);
  }

  return sourceList;
};

const Video = ({
  id,
  onError,
  onEncodingComplete,
  encodingFallback,
  previewModeAction,
  previewModeButtonSize,
  posterImage,
  encodingTitle,
  ...props
}) => {
  const [forceAutoplay, setForceAutoplay] = useState(false);
  const [previewMode, setPreviewMode] = useState(
    typeof previewModeAction !== 'undefined'
  );

  const prevStatus = useRef();
  const updateVideoTimer = useRef();

  const { data, loading, error, refetch } = useQuery(GET_VIDEO, {
    variables: { ref: id },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const [trackEventMutation] = useMutation(CREATE_VIDEO_TRACKING_EVENT);

  const globalId = data?.videoV2?.globalId;
  const videoStatus =
    data?.videoV2?.status === 'CREATED' || data?.videoV2?.status === 'ENCODING'
      ? 'encoding'
      : data?.videoV2?.status;

  const sources = getSources(data?.videoV2?.sources);
  const subtitles = data?.videoV2?.subtitles;
  const sprites = data?.videoV2?.sprites?.[0]?.sources;
  const thumbnails =
    data?.videoV2?.customThumbnails?.length > 0
      ? data?.videoV2?.customThumbnails
      : data?.videoV2?.defaultThumbnails;
  const hasError = (!loading && !data?.videoV2?.ref) || error;

  useEffect(() => {
    if (videoStatus === 'encoding') {
      updateVideoTimer.current = setInterval(() => refetch(), 5000);
    }

    if (
      prevStatus.current === 'encoding' &&
      videoStatus !== prevStatus.current
    ) {
      if (onEncodingComplete) {
        // returning an object thinking about future implementations using the status
        onEncodingComplete({
          id: id,
          sources: sources,
          status: videoStatus,
          sprites: sprites,
          thumbnails: thumbnails,
          urn: globalId,
          error: error,
        });
      }
    }

    prevStatus.current = videoStatus;

    return () => {
      if (updateVideoTimer?.current) {
        clearInterval(updateVideoTimer.current);
      }
    };
  }, [videoStatus]);

  const enableVideoAndPlay = () => {
    setPreviewMode(false);
    setForceAutoplay(true);
  };

  if (hasError) {
    onError && onError();

    return (
      <MessageContainer>
        <BodyCopy>Error loading video</BodyCopy>
      </MessageContainer>
    );
  }

  if (videoStatus === 'encoding') {
    const CustomEncoding = encodingFallback;
    return CustomEncoding ? (
      <CustomEncoding encodingTitle={encodingTitle} />
    ) : (
      <MessageContainer>
        <Encoding />
      </MessageContainer>
    );
  }

  if (loading || !sources || !sources.length) {
    return null;
  }

  if (previewMode) {
    return (
      <PreviewMode
        thumbnails={thumbnails}
        posterImage={posterImage}
        forceAutoplay={forceAutoplay}
        buttonSize={previewModeButtonSize}
        onClick={() => previewModeAction(enableVideoAndPlay)}
      />
    );
  }

  return (
    <React.Fragment>
      <Player
        id={id}
        sources={sources}
        thumbnails={thumbnails?.[0]?.sources}
        sprites={sprites}
        subtitles={subtitles}
        forceAutoplay={forceAutoplay}
        trackEventMutation={trackEventMutation}
        {...props}
      />
    </React.Fragment>
  );
};

Video.propTypes = {
  id: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  configuration: PropTypes.object,
  actions: PropTypes.func,
  onReady: PropTypes.func,
  onStart: PropTypes.func,
  onMute: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  posterImage: PropTypes.string,
  previewModeAction: PropTypes.func,
  previewModeButtonSize: PropTypes.number,
  encodingFallback: PropTypes.func,
  onEncodingComplete: PropTypes.func,
  encodingTitle: PropTypes.string,
  isStory: PropTypes.bool,
};

Video.defaultProps = {
  configuration: {},
  isStory: false,
};

export default Video;
